<script setup>
import { ref, useId, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  activeSort: {
    type: String,
    default: 'last_activity_at',
  },
  activeOrdering: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:sort']);

const { t } = useI18n();

const isMenuOpen = ref(false);
const root = ref(null);
const menuId = useId();
const closeMenu = () => {
  isMenuOpen.value = false;
  root.value?.querySelector('button')?.focus();
};
const toggleMenu = async () => {
  isMenuOpen.value = !isMenuOpen.value;
  if (isMenuOpen.value) {
    await nextTick();
    document.getElementById(menuId)?.querySelector('select')?.focus();
  }
};

const sortMenus = [
  {
    label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.SORT_BY.OPTIONS.NAME'),
    value: 'name',
  },
  {
    label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.SORT_BY.OPTIONS.EMAIL'),
    value: 'email',
  },
  {
    label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.SORT_BY.OPTIONS.COMPANY'),
    value: 'company_name',
  },
  {
    label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.SORT_BY.OPTIONS.COUNTRY'),
    value: 'country',
  },
  {
    label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.SORT_BY.OPTIONS.CITY'),
    value: 'city',
  },
  {
    label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.SORT_BY.OPTIONS.LAST_ACTIVITY'),
    value: 'last_activity_at',
  },
  {
    label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.SORT_BY.OPTIONS.CREATED_AT'),
    value: 'created_at',
  },
];

const orderingMenus = [
  {
    label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.ORDER.OPTIONS.ASCENDING'),
    value: '',
  },
  {
    label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.ORDER.OPTIONS.DESCENDING'),
    value: '-',
  },
];

const handleSortChange = value => {
  emit('update:sort', { sort: value, order: props.activeOrdering });
};

const handleOrderChange = value => {
  emit('update:sort', { sort: props.activeSort, order: value });
};
</script>

<template>
  <div ref="root" class="relative" @keydown.esc.stop.prevent="closeMenu">
    <Button
      icon="i-ph-arrows-down-up"
      :aria-label="t('CONTACTS_LAYOUT.HEADER.ACTIONS.SORT_BY.LABEL')"
      :aria-expanded="isMenuOpen"
      :aria-controls="isMenuOpen ? menuId : undefined"
      class="!h-11 !w-11 !rounded-xl"
      color="slate"
      size="sm"
      variant="ghost"
      :class="isMenuOpen ? 'bg-n-alpha-2' : ''"
      @click="toggleMenu"
    />
    <div
      v-if="isMenuOpen"
      :id="menuId"
      v-on-clickaway="() => (isMenuOpen = false)"
      class="absolute top-full mt-1 ltr:left-0 rtl:right-0 sm:ltr:left-auto sm:rtl:right-auto sm:ltr:right-0 sm:rtl:left-0 flex flex-col gap-4 bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak w-72 max-w-[calc(100vw-6rem)] sm:max-w-none rounded-2xl p-4 z-50"
    >
      <label class="flex flex-col gap-2 text-sm text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.SORT_BY.LABEL') }}
        <select
          :value="activeSort"
          class="!m-0 !h-11 !w-full !rounded-xl !border-n-weak !bg-n-solid-2 !text-n-slate-12"
          @change="handleSortChange($event.target.value)"
        >
          <option
            v-for="option in sortMenus"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </label>
      <label class="flex flex-col gap-2 text-sm text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.ORDER.LABEL') }}
        <select
          :value="activeOrdering"
          class="!m-0 !h-11 !w-full !rounded-xl !border-n-weak !bg-n-solid-2 !text-n-slate-12"
          @change="handleOrderChange($event.target.value)"
        >
          <option
            v-for="option in orderingMenus"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </label>
    </div>
  </div>
</template>
